/**
 * Configuration Panel Component
 *
 * Displays demo data configuration in expandable sections:
 * - Baseline configuration (AUV, wages, labor%, seeds)
 * - Seasonality profile (monthly multipliers)
 * - Weekday profile (daily multipliers)
 * - Unit-level overrides (if any exist)
 *
 * Uses lazy-loading: data is fetched only when section is expanded
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  Skeleton,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  ViewWeek as WeekIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { API } from 'aws-amplify';
import {
  GET_DEMO_BASELINE,
  GET_DEMO_SEASONALITY,
  GET_DEMO_WEEKDAY_PROFILE,
  LIST_DEMO_UNIT_CONFIGS
} from '../queries';

/**
 * ConfigurationPanel Component
 * @param {Object} props - Component props
 * @param {string} props.clientId - Client ID to fetch configuration for
 */
const ConfigurationPanel = ({ clientId }) => {
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState([]);

  // Data state
  const [baseline, setBaseline] = useState(null);
  const [seasonality, setSeasonality] = useState(null);
  const [weekdayProfile, setWeekdayProfile] = useState(null);
  const [unitConfigs, setUnitConfigs] = useState([]);

  // Loading state
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [seasonalityLoading, setSeasonalityLoading] = useState(false);
  const [weekdayLoading, setWeekdayLoading] = useState(false);
  const [unitConfigsLoading, setUnitConfigsLoading] = useState(false);

  // Error state
  const [baselineError, setBaselineError] = useState(null);
  const [seasonalityError, setSeasonalityError] = useState(null);
  const [weekdayError, setWeekdayError] = useState(null);
  const [unitConfigsError, setUnitConfigsError] = useState(null);

  /**
   * Handle section expansion
   * @param {string} section - Section identifier
   */
  const handleSectionChange = (section) => (event, isExpanded) => {
    if (isExpanded) {
      setExpandedSections([...expandedSections, section]);
    } else {
      setExpandedSections(expandedSections.filter(s => s !== section));
    }
  };

  /**
   * Fetch baseline configuration when section is expanded
   */
  useEffect(() => {
    const fetchBaseline = async () => {
      if (!clientId || !expandedSections.includes('baseline') || baseline) {
        return;
      }

      try {
        setBaselineLoading(true);
        setBaselineError(null);

        const result = await API.graphql({
          query: GET_DEMO_BASELINE,
          variables: { clientId }
        });

        setBaseline(result.data.getDemoBaseline);
      } catch (error) {
        console.error('Error fetching baseline:', error);
        setBaselineError(error.message || 'Failed to load baseline configuration');
      } finally {
        setBaselineLoading(false);
      }
    };

    fetchBaseline();
  }, [clientId, expandedSections, baseline]);

  /**
   * Fetch seasonality profile when section is expanded
   */
  useEffect(() => {
    const fetchSeasonality = async () => {
      if (!clientId || !expandedSections.includes('seasonality') || !baseline || seasonality) {
        return;
      }

      try {
        setSeasonalityLoading(true);
        setSeasonalityError(null);

        const result = await API.graphql({
          query: GET_DEMO_SEASONALITY,
          variables: { seasonalityKey: baseline.defaultSeasonalityKey }
        });

        setSeasonality(result.data.getDemoSeasonality);
      } catch (error) {
        console.error('Error fetching seasonality:', error);
        setSeasonalityError(error.message || 'Failed to load seasonality profile');
      } finally {
        setSeasonalityLoading(false);
      }
    };

    fetchSeasonality();
  }, [clientId, expandedSections, baseline, seasonality]);

  /**
   * Fetch weekday profile when section is expanded
   */
  useEffect(() => {
    const fetchWeekdayProfile = async () => {
      if (!clientId || !expandedSections.includes('weekday') || !baseline || weekdayProfile) {
        return;
      }

      try {
        setWeekdayLoading(true);
        setWeekdayError(null);

        const result = await API.graphql({
          query: GET_DEMO_WEEKDAY_PROFILE,
          variables: { profileKey: baseline.defaultWeekdayProfileKey }
        });

        setWeekdayProfile(result.data.getDemoWeekdayProfile);
      } catch (error) {
        console.error('Error fetching weekday profile:', error);
        setWeekdayError(error.message || 'Failed to load weekday profile');
      } finally {
        setWeekdayLoading(false);
      }
    };

    fetchWeekdayProfile();
  }, [clientId, expandedSections, baseline, weekdayProfile]);

  /**
   * Fetch unit configs when section is expanded
   */
  useEffect(() => {
    const fetchUnitConfigs = async () => {
      if (!clientId || !expandedSections.includes('unitConfigs') || unitConfigs.length > 0) {
        return;
      }

      try {
        setUnitConfigsLoading(true);
        setUnitConfigsError(null);

        // Fetch all unit configs with pagination
        let allConfigs = [];
        let nextToken = null;

        do {
          const result = await API.graphql({
            query: LIST_DEMO_UNIT_CONFIGS,
            variables: {
              clientId,
              limit: 1000,
              nextToken
            }
          });

          const items = result.data.listDemoUnitConfigs.items || [];
          allConfigs = [...allConfigs, ...items];
          nextToken = result.data.listDemoUnitConfigs.nextToken;
        } while (nextToken);

        setUnitConfigs(allConfigs);
      } catch (error) {
        console.error('Error fetching unit configs:', error);
        setUnitConfigsError(error.message || 'Failed to load unit configurations');
      } finally {
        setUnitConfigsLoading(false);
      }
    };

    fetchUnitConfigs();
  }, [clientId, expandedSections, unitConfigs.length]);

  // Show empty state if no client selected
  if (!clientId) {
    return (
      <Alert severity="info">
        Please select a client to view configuration
      </Alert>
    );
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Baseline Configuration */}
      <Accordion
        expanded={expandedSections.includes('baseline')}
        onChange={handleSectionChange('baseline')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.08)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ color: '#7C3AED' }} />
            <Typography sx={{ fontWeight: 600, color: '#7C3AED' }}>
              Baseline Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {baselineLoading ? (
            <Box>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : baselineError ? (
            <Alert severity="error">{baselineError}</Alert>
          ) : baseline ? (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>Annual Unit Volume (AUV)</td>
                    <td style={{ padding: '6px 8px' }}>${baseline.auv?.toLocaleString()}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>Average Check</td>
                    <td style={{ padding: '6px 8px' }}>${baseline.avgCheck?.toFixed(2)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>Labor %</td>
                    <td style={{ padding: '6px 8px' }}>{baseline.laborPct?.toFixed(1)}%</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>FOH Wage</td>
                    <td style={{ padding: '6px 8px' }}>${baseline.fohWage?.toFixed(2)}/hour</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>BOH Wage</td>
                    <td style={{ padding: '6px 8px' }}>${baseline.bohWage?.toFixed(2)}/hour</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>FOH/BOH Split</td>
                    <td style={{ padding: '6px 8px' }}>{baseline.fohBohSplit?.map(v => v.toFixed(1)).join(' / ')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>Noise Sigma (σ)</td>
                    <td style={{ padding: '6px 8px' }}>{baseline.noiseSigma?.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>Base Seed</td>
                    <td style={{ padding: '6px 8px' }}>{baseline.baseSeed}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          ) : (
            <Alert severity="warning">
              No baseline configuration found. Please seed configuration in the Data Management tab.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Seasonality Profile */}
      <Accordion
        expanded={expandedSections.includes('seasonality')}
        onChange={handleSectionChange('seasonality')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.08)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: '#7C3AED' }} />
            <Typography sx={{ fontWeight: 600, color: '#7C3AED' }}>
              Seasonality Profile
              {baseline && (
                <Chip
                  label={baseline.defaultSeasonalityKey}
                  size="small"
                  sx={{ ml: 1, backgroundColor: '#7C3AED', color: 'white' }}
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!baseline ? (
            <Alert severity="info">
              Expand Baseline Configuration first to load seasonality profile
            </Alert>
          ) : seasonalityLoading ? (
            <Box>
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : seasonalityError ? (
            <Alert severity="error">{seasonalityError}</Alert>
          ) : seasonality ? (
            <Box>
              {seasonality.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {seasonality.description}
                </Typography>
              )}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>
                      {months.map(month => (
                        <th key={month} style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600 }}>
                          {month}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {seasonality.monthly?.map((value, index) => (
                        <td key={index} style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {value.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Monthly multipliers applied to baseline sales values
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning">
              Seasonality profile not found
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Weekday Profile */}
      <Accordion
        expanded={expandedSections.includes('weekday')}
        onChange={handleSectionChange('weekday')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.08)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WeekIcon sx={{ color: '#7C3AED' }} />
            <Typography sx={{ fontWeight: 600, color: '#7C3AED' }}>
              Weekday Profile
              {baseline && (
                <Chip
                  label={baseline.defaultWeekdayProfileKey}
                  size="small"
                  sx={{ ml: 1, backgroundColor: '#7C3AED', color: 'white' }}
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!baseline ? (
            <Alert severity="info">
              Expand Baseline Configuration first to load weekday profile
            </Alert>
          ) : weekdayLoading ? (
            <Box>
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : weekdayError ? (
            <Alert severity="error">{weekdayError}</Alert>
          ) : weekdayProfile ? (
            <Box>
              {weekdayProfile.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {weekdayProfile.description}
                </Typography>
              )}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>
                      {weekdays.map(day => (
                        <th key={day} style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600 }}>
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {weekdayProfile.weekday?.map((value, index) => (
                        <td key={index} style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {value.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Daily multipliers applied to baseline sales values
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning">
              Weekday profile not found
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Unit-Level Overrides */}
      <Accordion
        expanded={expandedSections.includes('unitConfigs')}
        onChange={handleSectionChange('unitConfigs')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.08)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon sx={{ color: '#7C3AED' }} />
            <Typography sx={{ fontWeight: 600, color: '#7C3AED' }}>
              Unit-Level Overrides
              {unitConfigs.length > 0 && (
                <Chip
                  label={`${unitConfigs.length} overrides`}
                  size="small"
                  sx={{ ml: 1, backgroundColor: '#7C3AED', color: 'white' }}
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {unitConfigsLoading ? (
            <Box>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : unitConfigsError ? (
            <Alert severity="error">{unitConfigsError}</Alert>
          ) : unitConfigs.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(224, 224, 224, 1)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Entity ID</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Level</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Sales Mult</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Avg Check Mult</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Labor % Δ</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Wage Mult</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Seasonality</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Weekday</th>
                  </tr>
                </thead>
                <tbody>
                  {unitConfigs.map((config) => (
                    <tr key={config.entityId} style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                      <td style={{ padding: '6px 8px' }}>{config.entityId}</td>
                      <td style={{ padding: '6px 8px' }}>{config.orgLevel}</td>
                      <td style={{ padding: '6px 8px' }}>{config.salesMult !== null ? config.salesMult.toFixed(2) : '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{config.avgCheckMult !== null ? config.avgCheckMult.toFixed(2) : '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{config.laborPctDelta !== null ? `${config.laborPctDelta > 0 ? '+' : ''}${config.laborPctDelta.toFixed(1)}%` : '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{config.wageMult !== null ? config.wageMult.toFixed(2) : '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{config.seasonalityKey || '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{config.weekdayProfileKey || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Alert severity="info">
              No unit-level overrides configured. All locations use baseline configuration.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConfigurationPanel;
